import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { FinancialService } from '../../../../services/financial.service';
import { CustomerService } from '../../../../services/customer.service';
import { FinancialSummary } from '../../../../models/financialSummaryDto.model';
import { Customer } from '../../../../models/customer.model';
import { RestResponse } from '../../../../models/restResponse.mode';
import { DateRangeEnum } from '../../../../models/enums/dateRange.enum';
import { FinancialInstallment } from '../../../../models/financialInstallment.model';
import { ModalComponent } from '../../../../components/modal/modal.component';
import { TypePaymentService } from '../../../../services/typePayment.service';
import { TypePayment } from '../../../../models/typePayment.model';
import { CreateFinancialPaymentDto } from '../../../../models/financialPayment.model';
import { InstallmentSettlementPreviewDto } from '../../../../models/installment-settlement-preview.model';
import { SaleService } from '../../../../services/sale.service';
import { SaleItem } from '../../../../models/sale-item.model';

@Component({
  selector: 'app-view-customer-revenue',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule,
    InputTextModule,
    DropdownModule,
    CardModule,
    ButtonModule,
    PaginatorModule,
    TableModule,
    TagModule,
    TooltipModule,
    ModalComponent
  ],
  templateUrl: './list-customer-revenue.component.html'
})
export class ListCustomerRevenueComponent implements OnInit {
  customerName = 'Carregando...';
  customerId: number = 0;
  customer: Customer | null = null;
  total = 0;
  revenues: FinancialSummary[] = [];
  loading = false;
  
  // Paginação
  first = 0;
  rows = 10;
  totalRecords = 0;
  currentPage = 1;
  
  // Filtros
  searchTerm = '';
  currentStatus = 'todos';
  currentDateRange = DateRangeEnum.All;
  // Expansão e parcelas
  expanded: Record<number, boolean> = {};
  installmentsCache: Record<number, FinancialInstallment[]> = {};
  installmentsLoading: Record<number, boolean> = {};

  // Modal de quitação
  settleDialogVisible = false;
  settleSubmitting = false;
  selectedInstallment: FinancialInstallment | null = null;
  typePayments: TypePayment[] = [];
  typePaymentDropdownOptions: any[] = [];
  settleDate: string = new Date().toISOString().substring(0, 10);
  settleTypePaymentId: number | null = null;
  settleValue: number | null = null;
  settlePreview: InstallmentSettlementPreviewDto | null = null;
  settlePreviewLoading = false;

  // Modal de itens da venda
  saleItemsDialogVisible = false;
  saleItems: SaleItem[] = [];
  saleItemsLoading = false;
  saleIdInModal: number | null = null;
  saleItemsError: string | null = null;
  
  // Opções de filtro
  statusOptions = [
    { label: 'Todos', value: 'todos' },
    { label: 'Pendente', value: 'pendente' },
    { label: 'Normal', value: 'normal' },
    { label: 'Quitado', value: 'quitado' },
    { label: 'Cancelado', value: 'cancelado' }
  ];
  
  dateRangeOptions = [
    { label: 'Todos', value: DateRangeEnum.All },
    { label: 'Este mês', value: DateRangeEnum.ThisMonth },
    { label: 'Mês passado', value: DateRangeEnum.LastMonth },
    { label: 'Últimos 3 meses', value: DateRangeEnum.LastThreeMonths },
    { label: 'Últimos 6 meses', value: DateRangeEnum.LastSixMonths },
    { label: 'Este ano', value: DateRangeEnum.ThisYear },
    { label: 'Ano passado', value: DateRangeEnum.LastYear }
  ];

  constructor(
    private financialService: FinancialService,
    private customerService: CustomerService,
    private typePaymentService: TypePaymentService,
    private saleService: SaleService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location
  ) {}

  ngOnInit(): void {
    // Inicializar valores de paginação
    this.first = 0;
    this.rows = 10;
    this.totalRecords = 0;
    this.currentPage = 1;
    
    // Pegar o customerId da rota
    this.route.params.subscribe(params => {
      this.customerId = +params['id']; // Converte para number
      this.loadCustomer();
      this.loadRevenues();
    });

    this.loadTypePayments();
  }

  loadCustomer(): void {
    // Tentar buscar pelo endpoint específico primeiro
    this.customerService.getById(this.customerId).subscribe({
      next: (customer: RestResponse<Customer>) => {
        this.customer = customer.data;
        this.customerName = customer.data.fullName;
      },
      error: (error) => {
        console.error('Erro ao carregar cliente:', error);
        // Fallback: buscar na listagem e filtrar
        this.loadCustomerFromList();
      }
    });
  }

  loadTypePayments(): void {
    this.typePaymentService.getAll().subscribe({
      next: (response) => {
        if (response && response.data) {
          this.typePayments = response.data;
          this.typePaymentDropdownOptions = [
            { label: 'Selecione um método de pagamento', value: '' },
            ...this.typePayments.map(tp => ({ label: tp.description, value: tp.id }))
          ];
        } else {
          this.typePayments = [];
          this.typePaymentDropdownOptions = [
            { label: 'Selecione um método de pagamento', value: '' }
          ];
        }
      },
      error: () => {
        this.typePayments = [];
        this.typePaymentDropdownOptions = [
          { label: 'Selecione um método de pagamento', value: '' }
        ];
      }
    });
  }

  loadCustomerFromList(): void {
    this.customerService.getAll('', null, 1).subscribe({
      next: (response) => {
        if (response && response.data) {
          const customer = response.data.find(c => c.id === this.customerId);
          if (customer) {
            this.customer = customer;
            this.customerName = customer.fullName;
          } else {
            this.customerName = 'Cliente não encontrado';
          }
        } else {
          this.customerName = 'Cliente não encontrado';
        }
      },
      error: (error) => {
        console.error('Erro ao carregar lista de clientes:', error);
        this.customerName = 'Cliente não encontrado';
      }
    });
  }

  loadRevenues(): void {
    this.loading = true;
    this.financialService.getFinancialSummary(
      this.searchTerm, 
      this.currentStatus, 
      this.currentPage,
      this.customerId, 
      this.currentDateRange

    ).subscribe({
      next: (response: RestResponse<FinancialSummary[]>) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.revenues = response.data;
          // Usar a estrutura correta da paginação
          if (response.pagination) {
            this.totalRecords = response.pagination.totalRecords || 0;
            this.currentPage = response.pagination.currentPage || 1;
          } else {
            this.totalRecords = 0;
            this.currentPage = 1;
          }
        } else {
          this.revenues = [];
          this.totalRecords = 0;
          this.currentPage = 1;
        }
        this.calculateTotal();
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar receitas:', error);
        this.revenues = [];
        this.totalRecords = 0;
        this.currentPage = 1;
        this.calculateTotal();
        this.loading = false;
      }
    });
  }

  calculateTotal(): void {
    if (this.revenues && Array.isArray(this.revenues)) {
      this.total = this.revenues.reduce((sum, item) => sum + item.total, 0);
    } else {
      this.total = 0;
    }
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'quitado':
        return 'success';
      case 'pendente':
        return 'warn';
      case 'normal':
        return 'info';
      case 'cancelado':
        return 'danger';
      default:
        return 'info';
    }
  }

  // Filtros
  onSearchChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.loadRevenues();
  }

  onStatusChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.loadRevenues();
  }

  onDateRangeChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.loadRevenues();
  }

  // Paginação
  onPageChange(event: any): void {
    this.first = event.first || 0;
    this.rows = event.rows || 10;
    this.currentPage = Math.floor(this.first / this.rows) + 1;
    this.loadRevenues();
  }

  // Ações
  print(id: number) {
    this.financialService.exportA4(id).subscribe({
      next: (file) => {
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      },
      error: (error) => {
        console.log(error);
      }
    });
  }

  goBack(){
    this.location.back();
  }

  printHistory(){
    this.financialService.exportHistoryA4(this.customerId).subscribe({
      next: (data) => {
        const blob = new Blob([data], { type: 'application/pdf' }); 
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      },
      error: (error) => {
        console.error('Error generating PDF:', error);
      }
    });
  }

  cancel(id: number) {
    // abre modal de confirmação
    this.cancelTargetId = id;
    this.cancelDialogVisible = true;
  }

  // Modal: abrir, confirmar, fechar
  openSettleModal(inst: FinancialInstallment): void {
    if (inst.paid || inst.canceled) return;
    this.selectedInstallment = inst;
    this.settleDate = new Date().toISOString().substring(0, 10);
    this.settleTypePaymentId = null;
    this.settleValue = inst.remainingValue ?? inst.value;
    this.settlePreview = null;
    this.settleDialogVisible = true;
    this.refreshSettlePreview();
  }

  closeSettleModal(): void {
    this.settleDialogVisible = false;
    this.settlePreview = null;
    this.settlePreviewLoading = false;
  }

  onSettleDateChange(): void {
    this.refreshSettlePreview();
  }

  refreshSettlePreview(): void {
    if (!this.selectedInstallment) {
      return;
    }
    this.settlePreviewLoading = true;
    this.financialService
      .settlementPreview(this.selectedInstallment.financialId, this.selectedInstallment.id, this.settleDate)
      .subscribe({
        next: (resp) => {
          this.settlePreview = resp.data ?? null;
          this.settlePreviewLoading = false;
          if (this.settlePreview) {
            this.settleValue = this.settlePreview.suggestedTotal;
          }
        },
        error: () => {
          this.settlePreview = null;
          this.settlePreviewLoading = false;
          this.settleValue = this.getSettleRemainingBefore();
        },
      });
  }

  confirmSettle(): void {
    if (!this.selectedInstallment || this.settleTypePaymentId == null || this.settleValue == null) {
      return;
    }
    if (this.settlePreviewLoading) {
      return;
    }
    const payload: CreateFinancialPaymentDto = {
      FinancialId: this.selectedInstallment.financialId,
      InstallmentId: this.selectedInstallment.id,
      Date: new Date(this.settleDate),
      TypePaymentId: this.settleTypePaymentId,
      ValuePaied: this.settleValue ?? 0
    };
    this.settleSubmitting = true;
    this.installmentsLoading[this.selectedInstallment.financialId] = true;
    this.financialService.settleInstallment(payload).subscribe({
      next: () => {
        const financialId = this.selectedInstallment!.financialId;
        this.financialService.getInstallments(financialId).subscribe({
          next: (resp) => {
            const list = resp?.data ?? [];
            list.sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
            this.installmentsCache[financialId] = list;
            this.installmentsLoading[financialId] = false;
          },
          error: () => {
            this.installmentsLoading[financialId] = false;
          }
        });
        this.loadRevenues();
        this.settleSubmitting = false;
        this.settleDialogVisible = false;
      },
      error: () => {
        this.settleSubmitting = false;
        this.installmentsLoading[this.selectedInstallment!.financialId] = false;
      }
    });
  }

  // Helpers de exibição do modal de quitação
  getSettleOriginalValue(): number {
    return this.selectedInstallment ? Number(this.selectedInstallment.value ?? 0) : 0;
  }

  getSettleRemainingBefore(): number {
    if (!this.selectedInstallment) return 0;
    const original = this.getSettleOriginalValue();
    const remaining = this.selectedInstallment.remainingValue != null
      ? Number(this.selectedInstallment.remainingValue)
      : original;
    return Math.max(0, remaining);
  }

  getSettlePaidSoFar(): number {
    const original = this.getSettleOriginalValue();
    const remaining = this.getSettleRemainingBefore();
    return Math.max(0, original - remaining);
  }

  getSettleRemainingPercent(): number {
    const original = this.getSettleOriginalValue();
    const remaining = this.getSettleRemainingBefore();
    if (original <= 0) return 0;
    return Math.max(0, Math.min(100, +(remaining * 100 / original).toFixed(2)));
  }

  getSettleRemainingAfter(): number {
    const remainingBefore = this.getSettleRemainingBefore();
    const interest = this.settlePreview?.interestAmount ?? 0;
    const payNow = Number(this.settleValue ?? 0);
    const principalPortion = Math.max(0, payNow - interest);
    return Math.max(0, remainingBefore - (Number.isNaN(principalPortion) ? 0 : principalPortion));
  }

  getSettleMinPayable(): number {
    const interest = this.settlePreview?.interestAmount ?? 0;
    if (interest > 0) {
      return Math.round(interest * 100) / 100;
    }
    return 0.01;
  }

  getSettleMaxPayable(): number {
    const remainingBefore = this.getSettleRemainingBefore();
    const interest = this.settlePreview?.interestAmount ?? 0;
    return Math.round((remainingBefore + interest) * 100) / 100;
  }

  onSettleValueChange(value: any): void {
    const parsed = Number(value ?? 0);
    if (Number.isNaN(parsed)) {
      this.settleValue = this.getSettleMinPayable();
      return;
    }
    const min = this.getSettleMinPayable();
    const max = this.getSettleMaxPayable();
    this.settleValue = Math.max(min, Math.min(max, parsed));
  }

  enforceSettleInput(event: any): void {
    const inputEl = event?.target as HTMLInputElement;
    if (!inputEl) return;
    const parsed = Number(inputEl.value ?? 0);
    const min = this.getSettleMinPayable();
    const max = this.getSettleMaxPayable();
    const clamped = Math.max(min, Math.min(max, Number.isNaN(parsed) ? min : parsed));
    if (parsed !== clamped) {
      inputEl.value = String(clamped);
    }
    this.settleValue = clamped;
  }

  viewDetails(id: number) {
    // Alterna expansão
    const isExpanding = !this.expanded[id];
    this.expanded[id] = isExpanding;

    if (isExpanding && !this.installmentsCache[id]) {
      this.installmentsLoading[id] = true;
      this.financialService.getInstallments(id).subscribe({
        next: (resp) => {
          const list = resp?.data ?? [];
          list.sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
          this.installmentsCache[id] = list;
          this.installmentsLoading[id] = false;
        },
        error: () => {
          this.installmentsCache[id] = [];
          this.installmentsLoading[id] = false;
        }
      });
    }
  }

  createRevenue() {
    this.router.navigate(['/app/financial/new'], { queryParams: { customerId: this.customerId } });
  }

  openSaleItemsModal(item: FinancialSummary): void {
    this.saleItemsDialogVisible = true;
    this.saleItems = [];
    this.saleItemsLoading = true;
    this.saleIdInModal = item.saleId ?? null;
    this.saleItemsError = null;

    if (item.saleId) {
      this.saleService.getById(item.saleId).subscribe({
        next: (sale) => {
          this.saleItems = sale.saleItems ?? [];
          this.saleItemsLoading = false;
        },
        error: () => {
          this.saleItems = [];
          this.saleItemsLoading = false;
          this.closeSaleItemsModal();
        }
      });
    } else {
      this.saleItemsError = 'Esta receita não está vinculada a uma venda.';
      this.saleItemsLoading = false;
    }
  }

  closeSaleItemsModal(): void {
    this.saleItemsDialogVisible = false;
    this.saleItems = [];
    this.saleIdInModal = null;
    this.saleItemsError = null;
  }

  // Cancel modal state/handlers
  cancelDialogVisible = false;
  cancelSubmitting = false;
  cancelTargetId: number | null = null;
  closeCancelModal(): void {
    this.cancelDialogVisible = false;
    this.cancelTargetId = null;
  }
  confirmCancel(): void {
    if (this.cancelTargetId == null) { return; }
    this.cancelSubmitting = true;
    this.financialService.cancel(this.cancelTargetId).subscribe({
      next: () => {
        this.cancelSubmitting = false;
        this.cancelDialogVisible = false;
        this.cancelTargetId = null;
        // Atualiza a listagem e dashboard
        this.loadRevenues();
      },
      error: () => {
        this.cancelSubmitting = false;
      }
    });
  }
} 